/**
 * AP Invoices Demo — logistics cross-link card.
 *
 * THROWAWAY DEMO CODE. Shows that an invoice's supporting documents arrived in a
 * SEPARATE carrier email thread, the AI reason the proof-of-delivery was mapped
 * to the PO, and a link to the related invoice (product ↔ freight). Renders
 * nothing when the demo has no logistics link for this invoice.
 */

import { useNavigate } from '@tanstack/react-router';
import { TruckIcon, EnvelopeIcon, ArrowRightIcon } from '@phosphor-icons/react';
import { T, Var } from 'gt-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import paths from '@/paths';
import { useApLogisticsLink } from './provenance';

export function LogisticsLinkCard({ invoiceId }: { invoiceId: string }) {
  const navigate = useNavigate();
  const link = useApLogisticsLink(invoiceId);

  if (!link) return null;

  const isFreight = link.relatedKind === 'freight';

  return (
    <Card className="mx-1 border-sky-200">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <TruckIcon className="size-4 text-sky-600" weight="fill" />
          <T>Logistics &amp; freight</T>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 pt-0 text-xs">
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-between"
          onClick={() =>
            navigate({
              to: paths.authenticated.accountsPayable.invoice,
              params: { invoiceId: link.relatedInvoiceId },
              search: true,
              viewTransition: true,
            })
          }
        >
          <span className="flex items-center gap-1.5">
            <TruckIcon className="size-3.5" />
            {isFreight ? (
              <T>
                View freight invoice <Var>{link.relatedInvoiceNumber}</Var>
              </T>
            ) : (
              <T>
                View product invoice <Var>{link.relatedInvoiceNumber}</Var>
              </T>
            )}
          </span>
          <ArrowRightIcon className="size-3.5" />
        </Button>

        <div className="flex items-start gap-1.5 rounded-md bg-sky-50 px-2 py-1.5 text-sky-700">
          <EnvelopeIcon className="mt-0.5 size-3.5 shrink-0" />
          <span>
            <T>
              POD + freight invoice arrived in a separate{' '}
              <Var>{link.carrierName}</Var> email and were auto-linked.
            </T>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
